import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Star, ThumbsUp, ThumbsDown, Minus, Building2, MapPin, Filter, CheckCircle2, Calendar, Tag, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
// Region options
const regions = [{
  id: 'konzern',
  name: 'Gesamtkonzern',
  icon: Building2
}, {
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'oesterreich',
  name: 'Österreich'
}, {
  id: 'schweiz',
  name: 'Schweiz'
}, {
  id: 'tschechien',
  name: 'Tschechien'
}, {
  id: 'ungarn',
  name: 'Ungarn'
}, {
  id: 'polen',
  name: 'Polen'
}, {
  id: 'italien',
  name: 'Italien'
}];
// Topic colors and labels
const topicConfig: Record<string, {
  label: string;
  color: string;
}> = {
  verfuegbarkeit: {
    label: 'Verfügbarkeit',
    color: 'bg-orange-100 text-orange-700'
  },
  personal: {
    label: 'Personal',
    color: 'bg-purple-100 text-purple-700'
  },
  beratung: {
    label: 'Beratung',
    color: 'bg-green-100 text-green-700'
  },
  preise: {
    label: 'Preise',
    color: 'bg-blue-100 text-blue-700'
  },
  sortiment: {
    label: 'Sortiment',
    color: 'bg-indigo-100 text-indigo-700'
  },
  garten: {
    label: 'Gartenbereich',
    color: 'bg-cyan-100 text-cyan-700'
  },
  service: {
    label: 'Service',
    color: 'bg-pink-100 text-pink-700'
  },
  zuschnitt: {
    label: 'Zuschnitt',
    color: 'bg-emerald-100 text-emerald-700'
  },
  parkplatz: {
    label: 'Parkplatz',
    color: 'bg-amber-100 text-amber-700'
  },
  atmosphaere: {
    label: 'Atmosphäre',
    color: 'bg-violet-100 text-violet-700'
  }
};
// Mock review data with all sources
const allReviews = [{
  id: 1,
  region: 'deutschland',
  store: 'OBI Markt Berlin-Mitte',
  author: 'Thomas M.',
  date: '2024-01-28',
  stars: 1,
  text: 'Aktionsartikel waren schon am ersten Tag ausverkauft. Personal wirkte unmotiviert und unfreundlich.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'verfuegbarkeit'
}, {
  id: 2,
  region: 'oesterreich',
  store: 'OBI MachBar! Wien-Donaustadt',
  author: 'Sandra K.',
  date: '2024-01-28',
  stars: 2,
  text: 'Pflanzenabteilung hatte viele welke Produkte. Preise sind auch deutlich höher als bei der Konkurrenz.',
  sentiment: 'negative' as const,
  source: 'trustpilot' as const,
  topic: 'garten'
}, {
  id: 3,
  region: 'deutschland',
  store: 'OBI Markt München-Freiham',
  author: 'Michael B.',
  date: '2024-01-27',
  stars: 4,
  text: 'Gute Auswahl und freundliches Personal. Nur die Parkplatzsituation ist manchmal schwierig.',
  sentiment: 'positive' as const,
  source: 'obi-feedback' as const,
  topic: 'parkplatz'
}, {
  id: 4,
  region: 'deutschland',
  store: 'OBI MachBar! Köln',
  author: 'Lisa W.',
  date: '2024-01-27',
  stars: 1,
  text: 'Absolut inakzeptabel! Falsche Beratung beim Holzkauf erhalten.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'beratung'
}, {
  id: 5,
  region: 'schweiz',
  store: 'OBI Markt Zürich-Dietlikon',
  author: 'Peter H.',
  date: '2024-01-26',
  stars: 5,
  text: 'Bester OBI in der Gegend! Tolle Werkzeugauswahl und das Personal kennt sich aus.',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'sortiment'
}, {
  id: 6,
  region: 'deutschland',
  store: 'OBI Markt Hamburg-Altona',
  author: 'Anna S.',
  date: '2024-01-26',
  stars: 3,
  text: 'Durchschnittlicher Baumarkt. Nichts Besonderes, aber auch nichts Schlechtes.',
  sentiment: 'neutral' as const,
  source: 'obi-feedback' as const,
  topic: 'sortiment'
}, {
  id: 7,
  region: 'deutschland',
  store: 'OBI Markt Berlin-Mitte',
  author: 'Klaus R.',
  date: '2024-01-25',
  stars: 2,
  text: 'Der Laden ist oft unordentlich und die Regale nicht aufgefüllt.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'verfuegbarkeit'
}, {
  id: 8,
  region: 'oesterreich',
  store: 'OBI MachBar! Wien-Donaustadt',
  author: 'Maria G.',
  date: '2024-01-25',
  stars: 5,
  text: 'Hervorragend! Der neue Zuschnittservice ist fantastisch. Personal ist immer hilfsbereit.',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'zuschnitt'
}, {
  id: 9,
  region: 'deutschland',
  store: 'OBI Markt München-Freiham',
  author: 'Frank M.',
  date: '2024-01-24',
  stars: 4,
  text: 'Sehr gute Qualität bei den Pflanzen. Die Mitarbeiter sind freundlich.',
  sentiment: 'positive' as const,
  source: 'obi-feedback' as const,
  topic: 'garten'
}, {
  id: 10,
  region: 'schweiz',
  store: 'OBI Markt Zürich-Dietlikon',
  author: 'Sabine L.',
  date: '2024-01-24',
  stars: 3,
  text: 'Ganz okay, aber die Preise könnten etwas niedriger sein.',
  sentiment: 'neutral' as const,
  source: 'google' as const,
  topic: 'preise'
}, {
  id: 11,
  region: 'deutschland',
  store: 'OBI Markt Berlin-Mitte',
  author: 'Petra W.',
  date: '2024-01-23',
  stars: 5,
  text: 'Toller Service! Die Mitarbeiter haben mir geholfen, ein spezielles Ersatzteil zu finden.',
  sentiment: 'positive' as const,
  source: 'trustpilot' as const,
  topic: 'service'
}, {
  id: 12,
  region: 'deutschland',
  store: 'OBI MachBar! Köln',
  author: 'Hans M.',
  date: '2024-01-22',
  stars: 5,
  text: 'Beste Holzqualität in Köln! Die Beratung ist erstklassig.',
  sentiment: 'positive' as const,
  source: 'obi-feedback' as const,
  topic: 'beratung'
}, {
  id: 13,
  region: 'tschechien',
  store: 'OBI Markt Prag-Ruzyně',
  author: 'Uwe K.',
  date: '2024-01-22',
  stars: 4,
  text: 'Immer gute Ware und nettes Personal. Komme gerne wieder.',
  sentiment: 'positive' as const,
  source: 'google' as const,
  topic: 'beratung'
}, {
  id: 14,
  region: 'polen',
  store: 'OBI Markt Warschau-Ursynów',
  author: 'Gabi F.',
  date: '2024-01-21',
  stars: 3,
  text: 'Normale Einkaufserfahrung, nichts Besonderes aufgefallen.',
  sentiment: 'neutral' as const,
  source: 'trustpilot' as const,
  topic: 'service'
}, {
  id: 15,
  region: 'italien',
  store: 'OBI Markt Mailand-Corsico',
  author: 'Martin S.',
  date: '2024-01-21',
  stars: 4,
  text: 'Gute Auswahl an Werkzeugen. Preise sind fair.',
  sentiment: 'positive' as const,
  source: 'obi-feedback' as const,
  topic: 'sortiment'
}, {
  id: 16,
  region: 'ungarn',
  store: 'OBI Markt Budapest-Soroksár',
  author: 'Heike L.',
  date: '2024-01-20',
  stars: 2,
  text: 'Zu wenig Kassen offen, musste ewig warten.',
  sentiment: 'negative' as const,
  source: 'google' as const,
  topic: 'verfuegbarkeit'
}];
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string): number {
  if (period === 'gesamt') return value;
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
// Source icons
const GoogleIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" data-id="element-397">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-398" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-399" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-400" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-401" />
  </svg>;
const TrustpilotIcon = () => <svg viewBox="0 0 24 24" className="w-4 h-4" data-id="element-402">
    <path fill="#00B67A" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" data-id="element-403" />
  </svg>;
const ObiIcon = () => <div className="w-4 h-4 rounded-full bg-obi-orange flex items-center justify-center" data-id="element-404">
    <span className="text-[8px] font-bold text-white" data-id="element-405">O</span>
  </div>;
const ITEMS_PER_PAGE = 10;
export function GeneralStimmenPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [sentimentFilter, setSentimentFilter] = useState<'all' | 'positive' | 'neutral' | 'negative'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'google' | 'trustpilot' | 'obi-feedback'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const rawRegionReviews = selectedRegion === 'konzern' ? allReviews : allReviews.filter(r => r.region === selectedRegion);
  const targetLength = applyTimeFactor(rawRegionReviews.length, selectedPeriod);
  const regionReviews = rawRegionReviews.slice(0, targetLength);
  const filteredReviews = regionReviews.filter(review => sentimentFilter === 'all' || review.sentiment === sentimentFilter).filter(review => sourceFilter === 'all' || review.source === sourceFilter).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const totalPages = Math.ceil(filteredReviews.length / ITEMS_PER_PAGE);
  const paginatedReviews = filteredReviews.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const handleFilterChange = (filterFn: () => void) => {
    filterFn();
    setCurrentPage(1);
  };
  const stats = {
    total: regionReviews.length,
    positive: regionReviews.filter(r => r.sentiment === 'positive').length,
    neutral: regionReviews.filter(r => r.sentiment === 'neutral').length,
    negative: regionReviews.filter(r => r.sentiment === 'negative').length,
    avgRating: regionReviews.reduce((acc, r) => acc + r.stars, 0) / regionReviews.length || 0
  };
  const getStarColor = (stars: number) => {
    if (stars <= 2) return 'text-[#E03C32]';
    if (stars <= 3) return 'text-[#FFD301]';
    return 'text-[#639754]';
  };
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'google':
        return <GoogleIcon data-id="element-406" />;
      case 'trustpilot':
        return <TrustpilotIcon data-id="element-407" />;
      case 'obi-feedback':
        return <ObiIcon data-id="element-408" />;
      default:
        return null;
    }
  };
  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'google':
        return 'Google';
      case 'trustpilot':
        return 'Trustpilot';
      case 'obi-feedback':
        return 'OBI Kundenfeedback';
      default:
        return source;
    }
  };
  const getSourceStyle = (source: string) => {
    switch (source) {
      case 'google':
        return 'bg-blue-50 text-blue-700';
      case 'trustpilot':
        return 'bg-green-50 text-green-700';
      case 'obi-feedback':
        return 'bg-obi-orange/10 text-obi-orange';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-409">
      <Card className="!p-4" data-id="element-410">
        <div className="flex flex-col gap-4" data-id="element-411">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-412">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-413">
              <Filter size={16} data-id="element-414" />
              <span data-id="element-415">Land:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-416">
              {regions.map(region => {
              const isSelected = selectedRegion === region.id;
              const Icon = region.icon;
              return <button key={region.id} onClick={() => handleFilterChange(() => setSelectedRegion(region.id))} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-obi-orange text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-417">
                    {Icon && <Icon size={14} data-id="element-418" />}
                    {region.name}
                  </button>;
            })}
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap" data-id="element-419">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-420">
              <Calendar size={16} data-id="element-421" />
              <span data-id="element-422">Zeitraum:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-423">
              {timePeriods.map(period => {
              const isSelected = selectedPeriod === period.id;
              return <button key={period.id} onClick={() => handleFilterChange(() => setSelectedPeriod(period.id))} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-obi-orange text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-424">
                    {period.label}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-425">
        <KPICard title="Durchschnittliche Bewertung" value={stats.avgRating.toFixed(1)} icon={<TrendingUp size={18} data-id="element-427" />} data-id="element-426" />
        <KPICard title="Gesamtbewertungen" value={stats.total.toString()} icon={<MessageSquare size={18} data-id="element-429" />} data-id="element-428" />
        <KPICard title="Positive Resonanz" value={stats.total > 0 ? `${Math.round(stats.positive / stats.total * 100)}%` : '0%'} icon={<ThumbsUp size={18} data-id="element-431" />} data-id="element-430" />
        <KPICard title="Negative Resonanz" value={stats.total > 0 ? `${Math.round(stats.negative / stats.total * 100)}%` : '0%'} icon={<ThumbsDown size={18} data-id="element-433" />} data-id="element-432" />
      </div>

      <Card className="!p-4" data-id="element-434">
        <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-435">
          <div className="flex items-center gap-3" data-id="element-436">
            <span className="text-sm font-medium text-gray-700" data-id="element-437">
              Sentiment:
            </span>
            <div className="flex gap-2" data-id="element-438">
              <button onClick={() => handleFilterChange(() => setSentimentFilter('all'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${sentimentFilter === 'all' ? 'bg-obi-orange text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-439">
                Alle ({regionReviews.length})
              </button>
              <button onClick={() => handleFilterChange(() => setSentimentFilter('positive'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sentimentFilter === 'positive' ? 'text-white' : 'text-[#639754] hover:opacity-80'}`} style={{
              backgroundColor: sentimentFilter === 'positive' ? '#639754' : '#e8f0e5'
            }} data-id="element-440">
                <ThumbsUp size={14} data-id="element-441" /> Positiv ({stats.positive})
              </button>
              <button onClick={() => handleFilterChange(() => setSentimentFilter('neutral'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sentimentFilter === 'neutral' ? 'text-gray-800' : 'text-[#b39500] hover:opacity-80'}`} style={{
              backgroundColor: sentimentFilter === 'neutral' ? '#FFD301' : '#fff9db'
            }} data-id="element-442">
                <Minus size={14} data-id="element-443" /> Neutral ({stats.neutral})
              </button>
              <button onClick={() => handleFilterChange(() => setSentimentFilter('negative'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sentimentFilter === 'negative' ? 'text-white' : 'text-[#E03C32] hover:opacity-80'}`} style={{
              backgroundColor: sentimentFilter === 'negative' ? '#E03C32' : '#fce8e7'
            }} data-id="element-444">
                <ThumbsDown size={14} data-id="element-445" /> Negativ ({stats.negative})
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-446">
            <span className="text-sm font-medium text-gray-700" data-id="element-447">Quelle:</span>
            <div className="flex gap-2" data-id="element-448">
              <button onClick={() => handleFilterChange(() => setSourceFilter('all'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${sourceFilter === 'all' ? 'bg-obi-orange text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-449">
                Alle
              </button>
              <button onClick={() => handleFilterChange(() => setSourceFilter('google'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sourceFilter === 'google' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`} data-id="element-450">
                <GoogleIcon data-id="element-451" /> Google
              </button>
              <button onClick={() => handleFilterChange(() => setSourceFilter('trustpilot'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sourceFilter === 'trustpilot' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`} data-id="element-452">
                <TrustpilotIcon data-id="element-453" /> Trustpilot
              </button>
              <button onClick={() => handleFilterChange(() => setSourceFilter('obi-feedback'))} className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${sourceFilter === 'obi-feedback' ? 'bg-obi-orange text-white' : 'bg-obi-orange/10 text-obi-orange hover:bg-obi-orange/20'}`} data-id="element-454">
                <ObiIcon data-id="element-455" /> Kundenfeedback
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between" data-id="element-456">
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-457">
          Alle konzernweiten Bewertungen
        </h2>
        <span className="text-sm text-gray-500" data-id="element-458">
          {filteredReviews.length} Bewertungen gefunden
        </span>
      </div>

      <div className="space-y-3" data-id="element-459">
        {paginatedReviews.length === 0 ? <Card className="!p-8 text-center" data-id="element-460">
            <p className="text-gray-500" data-id="element-461">
              Keine Bewertungen für diese Filter gefunden.
            </p>
          </Card> : paginatedReviews.map((review, index) => <motion.div key={review.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.03
      }} data-id="element-462">
              <Card className="!p-0 overflow-hidden" data-id="element-463">
                <div className="flex" data-id="element-464">
                  <div className="w-1.5" style={{
              backgroundColor: review.sentiment === 'positive' ? '#639754' : review.sentiment === 'neutral' ? '#FFD301' : '#E03C32'
            }} data-id="element-465" />
                  <div className="flex-1 p-4" data-id="element-466">
                    <div className="flex items-start justify-between gap-4" data-id="element-467">
                      <div className="flex-1 min-w-0" data-id="element-468">
                        <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-469">
                          <div className="flex items-center gap-1" data-id="element-470">
                            {[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < review.stars ? getStarColor(review.stars) : 'text-gray-200'} fill={i < review.stars ? 'currentColor' : 'none'} data-id="element-471" />)}
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${topicConfig[review.topic]?.color || 'bg-gray-100 text-gray-600'}`} data-id="element-472">
                            <Tag size={10} data-id="element-473" />{' '}
                            {topicConfig[review.topic]?.label || review.topic}
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getSourceStyle(review.source)}`} data-id="element-474">
                            {getSourceIcon(review.source)}{' '}
                            {getSourceLabel(review.source)}
                          </div>
                          <span className="text-sm text-gray-500" data-id="element-475">
                            {review.author}
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-476">•</span>
                          <span className="text-sm text-gray-500" data-id="element-477">
                            {new Date(review.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mb-2" data-id="element-478">
                          {review.store}
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-479">{review.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>)}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-center gap-2 pt-4" data-id="element-480">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-481">
            <ChevronLeft size={18} data-id="element-482" />
          </button>
          <div className="flex items-center gap-1" data-id="element-483">
            {Array.from({
          length: totalPages
        }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-obi-orange text-white' : 'hover:bg-gray-100 text-gray-600'}`} data-id="element-484">
                {page}
              </button>)}
          </div>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-485">
            <ChevronRight size={18} data-id="element-486" />
          </button>
        </div>}
    </div>;
}